import { useCartBooking } from '@/hooks/useCartBooking';
import React, { useMemo, useState, useEffect } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { formatApiError } from '@/lib/api';
import { MapPin, CalendarDays, Clock, Minus, Plus, Ticket, X } from 'lucide-react';
import { euro, fmtLongDate, fmtShortDate, categoryMeta, catColor } from '@/lib/eventsMock';
const SERVICE_FEE = 1; // commissione di servizio per biglietto (allineata al backend)

// legge la CSS var --primary ("H S% L%") e la converte in hsl() per il tema Stripe

// cache di loadStripe per publishable key (una sola inizializzazione per pk)

function Stepper({
  value,
  onChange,
  min = 1,
  max = 10
}) {
  return <div className="inline-flex items-center rounded-full border border-border bg-card" data-testid="ticket-quantity-stepper">
      <Button type="button" variant="ghost" size="icon" className="h-9 w-9 rounded-full" onClick={() => onChange(Math.max(min, value - 1))} disabled={value <= min} aria-label="Diminuisci">
        <Minus className="h-4 w-4" />
      </Button>
      <span className="w-8 text-center text-sm font-semibold tabular-nums" data-testid="ticket-quantity-value">{value}</span>
      <Button type="button" variant="ghost" size="icon" className="h-9 w-9 rounded-full" onClick={() => onChange(Math.min(max, value + 1))} disabled={value >= max} aria-label="Aumenta">
        <Plus className="h-4 w-4" />
      </Button>
    </div>;
}

/**
 * Selezione biglietti da aggiungere al carrello cliente.
 * Il pagamento, i punti e la ripartizione avvengono nel checkout comune.
 */

export function EventTicketDialog({
  event,
  open,
  onClose
}) {
  const addToUnifiedCart = useCartBooking();
  const [sessionId, setSessionId] = useState(null);
  const [ticketId, setTicketId] = useState(null);
  const [qty, setQty] = useState(1);
  const [terms, setTerms] = useState(false);
  const [creatingIntent, setCreatingIntent] = useState(false);

  useEffect(() => {
    if (event) {
      setSessionId(event.sessions?.[0]?.id || null);
      setTicketId(event.ticketTypes?.[0]?.id || null);
      setQty(1);
      setTerms(false);
      setCreatingIntent(false);
    }
  }, [event]);
  const meta = event ? categoryMeta(event.category) : null;
  const session = useMemo(() => event?.sessions?.find(s => s.id === sessionId) || null, [event, sessionId]);
  const headerSession = session || event?.sessions?.[0] || null;
  const ticket = useMemo(() => event?.ticketTypes?.find(t => t.id === ticketId) || null, [event, ticketId]);
  const subtotal = (ticket?.price || 0) * qty;
  const fees = SERVICE_FEE * qty;
  const total = subtotal + fees;
  if (!event) return null;
  const goCheckout = async () => {
    if (!terms) {
      toast.error('Devi accettare i termini per procedere');
      return;
    }
    if (creatingIntent) return;
    setCreatingIntent(true);
    try {
      if (await addToUnifiedCart('event', {
        event_id: event.id,
        session_id: sessionId,
        ticket_type_id: ticketId,
        qty
      })) onClose();
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail) || 'Impossibile aggiungere al carrello');
    } finally {
      setCreatingIntent(false);
    }
  };


  return <Dialog open={open} onOpenChange={o => {
    if (!o) onClose();
  }}>
      <DialogContent className="max-h-[92vh] gap-0 overflow-hidden rounded-2xl border-border p-0 sm:max-w-lg" data-testid="evento-detail-dialog">
        {/* Header con immagine */}
        <div className="relative h-40 w-full overflow-hidden sm:h-48">
          <img src={event.cover} alt={event.title} className="h-full w-full object-cover" />
          <div className="absolute inset-0" style={{
          background: 'linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.55) 100%)'
        }} />
          <button type="button" onClick={onClose} className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-black/40 text-white backdrop-blur transition-colors hover:bg-black/60" aria-label="Chiudi" data-testid="evento-detail-close">
            <X className="h-4 w-4" />
          </button>
          <span className="absolute left-3 top-3 inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold text-white backdrop-blur" style={{
          backgroundColor: catColor(event.category, 0.85)
        }}>
            {meta.label}
          </span>
          <div className="absolute inset-x-4 bottom-3">
            <h2 className="font-display text-xl font-bold leading-tight text-white drop-shadow sm:text-2xl" data-testid="evento-detail-title">{event.title}</h2>
            <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-white/90" data-testid="evento-detail-meta">
              {headerSession && <span className="flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" />{fmtLongDate(headerSession.date)}</span>}
              {headerSession?.time && <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{headerSession.time}</span>}
              {event.location && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{event.location}</span>}
            </p>
          </div>
        </div>

        <ScrollArea className="max-h-[calc(92vh-12rem)]">
          <div className="p-5">
            <div className="space-y-5" data-testid="ticket-select-step">
                <p className="text-sm leading-relaxed text-muted-foreground">{event.description}</p>

                {/* Sessione */}
                <div>
                  <Label className="mb-2 block text-sm font-semibold text-foreground">Scegli la data</Label>
                  <div className="grid grid-cols-2 gap-2" data-testid="ticket-session-select">
                    {event.sessions.map(s => {
                  const active = s.id === sessionId;
                  return <button key={s.id} type="button" onClick={() => setSessionId(s.id)} data-testid={`ticket-session-${s.id}`} className={cn('flex flex-col items-start rounded-xl border p-3 text-left transition-colors', active ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'border-border hover:bg-accent/40')}>
                          <span className="text-sm font-semibold capitalize text-foreground">{fmtShortDate(s.date)} · {s.time}</span>
                          <span className={cn('mt-0.5 text-xs', s.left <= 15 ? 'font-medium' : 'text-muted-foreground')} style={s.left <= 15 ? {
                      color: catColor('sagre')
                    } : undefined}>
                            {s.left <= 15 ? `Ultimi ${s.left} posti` : `${s.left} posti disponibili`}
                          </span>
                        </button>;
                })}
                  </div>
                </div>

                {/* Tipo biglietto */}
                <div>
                  <Label className="mb-2 block text-sm font-semibold text-foreground">Tipo di biglietto</Label>
                  <div className="space-y-2" data-testid="ticket-type-select">
                    {event.ticketTypes.map(t => {
                  const active = t.id === ticketId;
                  return <button key={t.id} type="button" onClick={() => setTicketId(t.id)} data-testid={`ticket-type-${t.id}`} className={cn('flex w-full items-center justify-between rounded-xl border p-3 text-left transition-colors', active ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'border-border hover:bg-accent/40')}>
                          <span className="flex items-center gap-3">
                            <span className={cn('flex h-5 w-5 items-center justify-center rounded-full border', active ? 'border-primary' : 'border-muted-foreground/40')}>
                              {active && <span className="h-2.5 w-2.5 rounded-full bg-primary" />}
                            </span>
                            <span>
                              <span className="block text-sm font-semibold text-foreground">{t.label}</span>
                              <span className="block text-xs text-muted-foreground">{t.desc}</span>
                            </span>
                          </span>
                          <span className="text-sm font-semibold tabular-nums text-foreground">{euro(t.price)}</span>
                        </button>;
                })}
                  </div>
                </div>

                {/* Quantità */}
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-semibold text-foreground">Quantità</Label>
                  <Stepper value={qty} onChange={setQty} />
                </div>

                <Separator />

                {/* Riepilogo */}
                <div className="space-y-1.5" data-testid="ticket-price-summary">
                  <div className="flex justify-between text-sm text-muted-foreground"><span>{ticket?.label} × {qty}</span><span className="tabular-nums">{euro(subtotal)}</span></div>
                  <div className="flex justify-between text-sm text-muted-foreground"><span>Commissione di servizio</span><span className="tabular-nums">{euro(fees)}</span></div>
                  <div className="flex justify-between pt-1 text-base font-bold text-foreground"><span>Totale</span><span className="tabular-nums" data-testid="ticket-total">{euro(total)}</span></div>
                </div>

                <label className="flex items-start gap-2 text-xs text-muted-foreground">
                  <Checkbox checked={terms} onCheckedChange={v => setTerms(!!v)} data-testid="cart-selection-terms" />
                  <span>Accetto i termini del servizio e la politica di rimborso.</span>
                </label>
                <Button className="h-11 w-full rounded-full text-sm font-semibold" onClick={goCheckout} data-testid="ticket-buy-button">
                  <Ticket className="mr-2 h-4 w-4" /> Aggiungi al carrello · {euro(total)}
                </Button>
              </div>

            

            
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>;
}
export default EventTicketDialog;
